import { useEffect, useState } from 'react';
import { CaretDown, CaretUp, Warning, XCircle } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import Card from '../components/ui/Card';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useAuth } from '../hooks/useAuth';
import { useDelayedLoading } from '../hooks/useDelayedLoading';
import { api } from '../services/api';
import { CreateLogger } from '../services/logger';
import { CountLabel } from '../utils/format';

const logger = CreateLogger('pages/home');

const Home = () => {
    const { user } = useAuth();
    const [reputationIssues, setReputationIssues] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedUnhealthy, setExpandedUnhealthy] = useState(false);
    const [expandedWarning, setExpandedWarning] = useState(false);
    // Skeleton dos cards com anti-flash de 300ms (plano UX 04) — substitui o
    // antigo texto "Carregando..."; cargas rápidas não piscam skeleton.
    const showSkeleton = useDelayedLoading(loading);

    useEffect(() => {
        const fetchReputationIssues = async () => {
            try {
                setLoading(true);
                const data = await api.getReputationIssues();
                setReputationIssues(data);
            } catch (error) {
                logger.error('reputation_issues_fetch_failed', {
                    error: {
                        name: error?.name,
                        message: error?.message,
                    },
                });
                setReputationIssues({
                    unhealthy: { results: [] },
                    warning: { results: [] },
                    all: [],
                    total: 0,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchReputationIssues();
    }, []);

    const hasUnhealthy = reputationIssues && reputationIssues.unhealthy.results.length > 0;
    const hasWarning = reputationIssues && reputationIssues.warning.results.length > 0;
    const hasAnyIssues = hasUnhealthy || hasWarning;

    return (
        <div className="space-y-6">
            <PageHeader
                title={`Bem-vindo, ${user?.full_name || 'usuário'}!`}
                subtitle="Resumo geral da operação e alertas de reputação"
            />

            {showSkeleton ? (
                // Skeleton dos 2 cards (layout idêntico ao real → sem salto de
                // layout quando o conteúdo chega).
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <SkeletonCard />
                    <SkeletonCard />
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Card title="Notificações">
                        <p className="text-mono-600 dark:text-mono-400">Nenhuma notificação no momento.</p>
                    </Card>

                    <Card title="Pendências">
                        {loading ? null : hasAnyIssues ? (
                            <div className="space-y-3">
                                {hasUnhealthy && (
                                    <div className="space-y-2">
                                        <button
                                            type="button"
                                            className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 text-left transition-colors hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:hover:bg-red-900/30"
                                            onClick={() => setExpandedUnhealthy(!expandedUnhealthy)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <XCircle
                                                    className="text-red-500 dark:text-red-400"
                                                    size={20}
                                                    weight="fill"
                                                />
                                                <div>
                                                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                                        {CountLabel(
                                                            reputationIssues.unhealthy.results.length,
                                                            'anúncio',
                                                        )}{' '}
                                                        perdendo exposição
                                                    </p>
                                                    <p className="text-xs text-red-500 dark:text-red-400">
                                                        Itens que já estão perdendo exposição
                                                    </p>
                                                </div>
                                            </div>
                                            {expandedUnhealthy ? (
                                                <CaretUp className="text-red-500 dark:text-red-400" size={20} />
                                            ) : (
                                                <CaretDown className="text-red-500 dark:text-red-400" size={20} />
                                            )}
                                        </button>

                                        {expandedUnhealthy && (
                                            <div className="mt-2 max-h-96 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/10">
                                                <div className="flex flex-wrap gap-2">
                                                    {reputationIssues.unhealthy.results.map((itemId) => (
                                                        <span
                                                            key={itemId}
                                                            className="rounded border border-red-200 bg-red-100 px-2 py-1 text-xs text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200"
                                                        >
                                                            {itemId}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {hasWarning && (
                                    <div className="space-y-2">
                                        <button
                                            type="button"
                                            className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-left transition-colors hover:bg-yellow-100 dark:border-yellow-800 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30"
                                            onClick={() => setExpandedWarning(!expandedWarning)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Warning
                                                    className="text-yellow-600 dark:text-yellow-400"
                                                    size={20}
                                                    weight="fill"
                                                />
                                                <div>
                                                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                                        {CountLabel(reputationIssues.warning.results.length, 'anúncio')}{' '}
                                                        pode perder exposição
                                                    </p>
                                                    <p className="text-xs text-yellow-600 dark:text-yellow-400">
                                                        Ainda é possível recuperar
                                                    </p>
                                                </div>
                                            </div>
                                            {expandedWarning ? (
                                                <CaretUp className="text-yellow-600 dark:text-yellow-400" size={20} />
                                            ) : (
                                                <CaretDown className="text-yellow-600 dark:text-yellow-400" size={20} />
                                            )}
                                        </button>

                                        {expandedWarning && (
                                            <div className="mt-2 max-h-96 overflow-y-auto rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/10">
                                                <div className="flex flex-wrap gap-2">
                                                    {reputationIssues.warning.results.map((itemId) => (
                                                        <span
                                                            key={itemId}
                                                            className="rounded border border-yellow-200 bg-yellow-100 px-2 py-1 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200"
                                                        >
                                                            {itemId}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-mono-600 dark:text-mono-400">Nenhuma pendência no momento.</p>
                        )}
                    </Card>
                </div>
            )}
        </div>
    );
};

export default Home;
